import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { formatError } from "../lib/api";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Bienvenue !");
      navigate("/dashboard");
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl px-6 py-16" data-testid="login-page">
      <h1 className="font-serif text-4xl font-semibold text-[#101A42] sm:text-5xl">
        {t("login")}
      </h1>
      <p className="mt-3 text-lg text-[#4A4A4A]">{t("app_tagline")}</p>

      <form onSubmit={onSubmit} className="mt-10 space-y-6">
        <div>
          <Label htmlFor="email" className="mb-2 block text-lg font-medium">
            {t("email")}
          </Label>
          <Input
            id="email"
            data-testid="login-email-input"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-16 rounded-xl border-2 text-lg"
          />
        </div>
        <div>
          <Label htmlFor="password" className="mb-2 block text-lg font-medium">
            {t("password")}
          </Label>
          <Input
            id="password"
            data-testid="login-password-input"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-16 rounded-xl border-2 text-lg"
          />
        </div>

        {error && (
          <div
            data-testid="login-error"
            className="rounded-xl border-2 border-[#A762B1]/30 bg-[#A762B1]/10 p-4 text-[#6E3978]"
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          disabled={submitting}
          data-testid="login-submit-btn"
          className="min-h-[64px] w-full rounded-full bg-[#A762B1] text-xl font-semibold text-white hover:bg-[#B775C0]"
        >
          {submitting ? "..." : t("login_cta")}
        </Button>

        <p className="text-center text-lg text-[#4A4A4A]">
          <Link
            to="/forgot-password"
            data-testid="forgot-link"
            className="font-semibold text-[#101A42] underline underline-offset-4"
          >
            {t("forgot_password")}
          </Link>
        </p>

        <p className="text-center text-lg text-[#4A4A4A]">
          {t("no_account")}{" "}
          <Link
            to="/register"
            data-testid="goto-register-link"
            className="font-semibold text-[#101A42] underline underline-offset-4"
          >
            {t("register")}
          </Link>
        </p>
      </form>
    </div>
  );
}
